// Modèle de données de l'espace Super Admin (frontend uniquement).
// Aucune donnée fictive : tout démarre vide et ne contient que ce qui est réellement créé.
// Ce modèle prépare une future connexion à Supabase (tables comptes / journal_audit).

export type CompteType = "proprietaire" | "agence" | "utilisateur";

export type CompteStatut = "actif" | "en_attente" | "suspendu" | "desactive" | "refuse";

export type AuditStatut = "reussie" | "echouee";

export type AuditActionKey =
  | "creation"
  | "modification"
  | "activation"
  | "suspension"
  | "desactivation"
  | "reactivation"
  | "refus";

// ---------------------------------------------------------------------------
// Statuts des comptes
// ---------------------------------------------------------------------------

export const COMPTE_STATUT_LABELS: Record<CompteStatut, string> = {
  actif: "Actif",
  en_attente: "En attente",
  suspendu: "Suspendu",
  desactive: "Désactivé",
  refuse: "Refusé",
};

export const COMPTE_STATUT_ICONS: Record<CompteStatut, string> = {
  actif: "ri-checkbox-circle-line",
  en_attente: "ri-time-line",
  suspendu: "ri-pause-circle-line",
  desactive: "ri-forbid-2-line",
  refuse: "ri-close-circle-line",
};

export type BadgeTone = "primary" | "accent" | "secondary" | "neutral" | "danger" | "success";

export const COMPTE_STATUT_TONES: Record<CompteStatut, BadgeTone> = {
  actif: "secondary",
  en_attente: "accent",
  suspendu: "danger",
  desactive: "neutral",
  refuse: "danger",
};

export const COMPTE_STATUT_OPTIONS = (Object.keys(COMPTE_STATUT_LABELS) as CompteStatut[]).map((value) => ({
  value,
  label: COMPTE_STATUT_LABELS[value],
}));

// ---------------------------------------------------------------------------
// Formules (source de vérité unique : @/config/formules)
// ---------------------------------------------------------------------------

export type { FormuleCatalogue as Formule, FormuleLimites } from "@/config/formules";
export {
  FORMULES_CATALOGUE as FORMULES,
  LIMITE_LABELS,
  getFormuleConfig as getFormule,
  formatFormuleLimite as formatLimite,
} from "@/config/formules";

// ---------------------------------------------------------------------------
// Rattachements (relations affichées sur les fiches, alimentées plus tard
// par la base de données)
// ---------------------------------------------------------------------------

export interface RattachementProprietaire {
  id: string;
  nom: string;
  telephone: string;
}

export interface RattachementImmeuble {
  id: string;
  nom: string;
  ville: string;
}

export interface RattachementBien {
  id: string;
  reference: string;
  immeubleNom: string;
  loyer: number;
}

export interface RattachementLocataire {
  id: string;
  nom: string;
  telephone: string;
}

export interface RattachementContrat {
  id: string;
  reference: string;
  bienReference: string;
  loyer: number;
  statut: string;
}

export interface RattachementEmploye {
  id: string;
  nom: string;
  role: string;
}

export interface CompteRattachements {
  proprietaires: RattachementProprietaire[];
  immeubles: RattachementImmeuble[];
  biens: RattachementBien[];
  locataires: RattachementLocataire[];
  contrats: RattachementContrat[];
  employes: RattachementEmploye[];
}

export const EMPTY_RATTACHEMENTS: CompteRattachements = {
  proprietaires: [],
  immeubles: [],
  biens: [],
  locataires: [],
  contrats: [],
  employes: [],
};

// ---------------------------------------------------------------------------
// Paiements d'abonnement + activité du compte
// ---------------------------------------------------------------------------

export interface ComptePaiement {
  id: string;
  montant: number;
  methode: string;
  reference: string;
  date: string;
  /** Statut du paiement d'abonnement. Absent = considéré comme validé (paiement encaissé). */
  statut?: ComptePaiementStatut;
}

export type ComptePaiementStatut = "valide" | "en_attente" | "rejete" | "annule";

export const COMPTE_PAIEMENT_STATUT_LABELS: Record<ComptePaiementStatut, string> = {
  valide: "Validé",
  en_attente: "En attente",
  rejete: "Rejeté",
  annule: "Annulé",
};

export const COMPTE_PAIEMENT_STATUT_TONES: Record<ComptePaiementStatut, BadgeTone> = {
  valide: "secondary",
  en_attente: "accent",
  rejete: "danger",
  annule: "neutral",
};

export interface ActiviteEntry {
  id: string;
  action: string;
  description: string;
  icone: string;
  date: string;
}

// ---------------------------------------------------------------------------
// Compte
// ---------------------------------------------------------------------------

export interface Compte {
  id: string;
  type: CompteType;
  nom: string;
  email: string;
  telephone: string;
  ville: string;
  responsable: string;
  role: string;
  formuleId: string;
  statut: CompteStatut;
  notes: string;
  createdAt: string;
  derniereActivite: string;
  rattachements: CompteRattachements;
  paiements: ComptePaiement[];
  activite: ActiviteEntry[];
}

export interface CompteInput {
  nom: string;
  email: string;
  telephone: string;
  ville: string;
  responsable: string;
  role: string;
  formuleId: string;
  statut: CompteStatut;
  notes: string;
}

// ---------------------------------------------------------------------------
// Journal d'audit
// ---------------------------------------------------------------------------

export interface AuditEntry {
  id: string;
  administrateur: string;
  action: string;
  actionKey: AuditActionKey;
  compteId: string;
  compteNom: string;
  compteType: CompteType;
  statut: AuditStatut;
  date: string;
}

export const AUDIT_ACTION_LABELS: Record<AuditActionKey, string> = {
  creation: "Création",
  modification: "Modification",
  activation: "Activation",
  suspension: "Suspension",
  desactivation: "Désactivation",
  reactivation: "Réactivation",
  refus: "Refus",
};

export const AUDIT_ACTION_ICONS: Record<AuditActionKey, string> = {
  creation: "ri-add-circle-line",
  modification: "ri-edit-line",
  activation: "ri-checkbox-circle-line",
  suspension: "ri-pause-circle-line",
  desactivation: "ri-forbid-2-line",
  reactivation: "ri-restart-line",
  refus: "ri-close-circle-line",
};

export const AUDIT_STATUT_LABELS: Record<AuditStatut, string> = {
  reussie: "Réussie",
  echouee: "Échouée",
};

export const AUDIT_STATUT_TONES: Record<AuditStatut, BadgeTone> = {
  reussie: "secondary",
  echouee: "danger",
};

// ---------------------------------------------------------------------------
// Configuration par type de compte
// ---------------------------------------------------------------------------

export interface CompteTypeConfig {
  type: CompteType;
  label: string;
  singular: string;
  lowercase: string;
  icon: string;
  basePath: string;
  title: string;
  description: string;
}

export const COMPTE_TYPE_CONFIG: Record<CompteType, CompteTypeConfig> = {
  proprietaire: {
    type: "proprietaire",
    label: "Propriétaires",
    singular: "Propriétaire",
    lowercase: "propriétaire",
    icon: "ri-user-star-line",
    basePath: "/super-admin/proprietaires",
    title: "Propriétaires",
    description:
      "Gérez les comptes propriétaires de la plateforme : statut, coordonnées et rattachements (immeubles, biens, locataires, contrats).",
  },
  agence: {
    type: "agence",
    label: "Agences",
    singular: "Agence",
    lowercase: "agence",
    icon: "ri-building-2-line",
    basePath: "/super-admin/agences",
    title: "Agences",
    description:
      "Gérez les comptes agences : statut, formule d'abonnement et rattachements (propriétaires, immeubles, biens, locataires, employés).",
  },
  utilisateur: {
    type: "utilisateur",
    label: "Utilisateurs",
    singular: "Utilisateur",
    lowercase: "utilisateur",
    icon: "ri-user-settings-line",
    basePath: "/super-admin/utilisateurs",
    title: "Utilisateurs",
    description:
      "Gérez les comptes utilisateurs de la plateforme : statut, coordonnées et rôle. Chaque action administrative est tracée dans le journal d'audit.",
  },
};

export const UTILISATEUR_ROLE_OPTIONS = [
  { value: "dg_proprietaire", label: "Propriétaire" },
  { value: "dg_agence", label: "Responsable d'agence" },
  { value: "employe_agence", label: "Employé d'agence" },
];

export function utilisateurRoleLabel(role: string): string {
  return UTILISATEUR_ROLE_OPTIONS.find((option) => option.value === role)?.label ?? (role || "Rôle non défini");
}

// Identité de repli neutre : l'espace affiche le Super Admin réellement connecté.
export const SUPER_ADMIN_USER = {
  name: "",
  role: "Super administrateur",
  initials: "",
};

/**
 * Modèle de données prévu pour Supabase.
 * Table `comptes` : id uuid, type, nom, email, telephone, ville, responsable,
 * role, formule_id, statut, notes, created_at.
 * Table `journal_audit` : id uuid, administrateur_id, action, compte_id,
 * statut, created_at.
 * Les rattachements seront calculés via les relations existantes
 * (immeubles, biens, locataires, contrats, employés) au moment du branchement.
 */